import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Download, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { STORY_T } from '../../data/generatorsI18n';

const THEMES = [
  { id: 'peach', a: '#fde8d7', b: '#f7c8b0', text: '#3a2a15' },
  { id: 'lilac', a: '#efe6fb', b: '#d9c8f3', text: '#2e2346' },
  { id: 'sage', a: '#e7f0e3', b: '#c8dcc0', text: '#233a26' },
  { id: 'sky', a: '#e3f0fb', b: '#bcd9f2', text: '#1b3350' },
  { id: 'night', a: '#2b2233', b: '#15111a', text: '#f5ecff' },
];
const FONT = "'Outfit', 'Segoe UI Symbol', 'Noto Sans Math', 'Apple Symbols', 'Cambria Math', sans-serif";
const W = 1080; const H = 1920;

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function wrap(ctx, text, max) {
  const out = [];
  text.split('\n').forEach((para) => {
    let line = '';
    para.split(' ').forEach((w) => {
      const test = line ? `${line} ${w}` : w;
      if (ctx.measureText(test).width > max && line) { out.push(line); line = w; } else line = test;
    });
    out.push(line);
  });
  return out;
}

export function drawStory(canvas, { bio, username, initial, theme, caption, stats, strings }) {
  const ctx = canvas.getContext('2d');
  canvas.width = W; canvas.height = H;
  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, theme.a); g.addColorStop(1, theme.b);
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);

  // decorative dots
  ctx.fillStyle = theme.id === 'night' ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.45)';
  for (let y = 40; y < H; y += 60) for (let x = 40; x < W; x += 60) { ctx.beginPath(); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill(); }

  // caption
  ctx.fillStyle = theme.text; ctx.textAlign = 'center';
  ctx.font = `600 54px ${FONT}`;
  ctx.fillText(caption, W / 2, 250);

  // measure bio to size the card
  const cardX = 90; const cardW = W - 180; const pad = 60;
  ctx.font = `400 40px ${FONT}`;
  const lines = wrap(ctx, bio, cardW - pad * 2);
  const bioH = lines.length * 56;
  const cardY = 360; const cardH = 470 + bioH + 420;

  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.18)'; ctx.shadowBlur = 60; ctx.shadowOffsetY = 24;
  ctx.fillStyle = '#ffffff'; rr(ctx, cardX, cardY, cardW, cardH, 48); ctx.fill();
  ctx.restore();

  // avatar ring
  const ax = cardX + pad + 90; const ay = cardY + pad + 90;
  const ring = ctx.createLinearGradient(ax - 90, ay - 90, ax + 90, ay + 90);
  ring.addColorStop(0, '#f9ce34'); ring.addColorStop(0.5, '#ee2a7b'); ring.addColorStop(1, '#6228d7');
  ctx.fillStyle = ring; ctx.beginPath(); ctx.arc(ax, ay, 92, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(ax, ay, 82, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#efe6da'; ctx.beginPath(); ctx.arc(ax, ay, 74, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#b85f12'; ctx.font = `600 72px 'Cormorant Garamond', serif`; ctx.textBaseline = 'middle';
  ctx.fillText(initial, ax, ay + 4);
  ctx.textBaseline = 'alphabetic';

  // stats
  const sx0 = ax + 150; const sw = (cardX + cardW - pad - sx0) / 3;
  stats.forEach(([v, l], i) => {
    const cx = sx0 + sw * i + sw / 2;
    ctx.fillStyle = '#0f172a'; ctx.font = `700 42px ${FONT}`; ctx.fillText(v, cx, ay - 4);
    ctx.fillStyle = '#64748b'; ctx.font = `400 28px ${FONT}`; ctx.fillText(l, cx, ay + 40);
  });

  // username + bio
  ctx.textAlign = 'left';
  let y = ay + 170;
  ctx.fillStyle = '#0f172a'; ctx.font = `700 38px ${FONT}`; ctx.fillText(username, cardX + pad, y);
  y += 64;
  ctx.fillStyle = '#1e293b'; ctx.font = `400 40px ${FONT}`;
  lines.forEach((l) => { ctx.fillText(l, cardX + pad, y); y += 56; });

  // buttons
  y += 30;
  const bw = (cardW - pad * 2 - 20) / 2;
  strings.buttons.forEach((t, i) => {
    const bx = cardX + pad + i * (bw + 20);
    ctx.fillStyle = '#f1f5f9'; rr(ctx, bx, y, bw, 76, 18); ctx.fill();
    ctx.fillStyle = '#0f172a'; ctx.font = `600 30px ${FONT}`; ctx.textAlign = 'center';
    ctx.fillText(t, bx + bw / 2, y + 48);
  });
  ctx.textAlign = 'left';

  // grid
  y += 116;
  const cols = ['#f3e3cf', '#e9d5c0', '#f7ece0', '#e6dccf', '#f1e0d6', '#ede4d8'];
  const gw = (cardW - pad * 2 - 16) / 3; const gh = (cardY + cardH - pad - y - 8) / 2;
  cols.forEach((c, i) => {
    const gx = cardX + pad + (i % 3) * (gw + 8); const gy = y + Math.floor(i / 3) * (gh + 8);
    ctx.fillStyle = c; rr(ctx, gx, gy, gw, gh, 12); ctx.fill();
  });

  // footer
  ctx.textAlign = 'center'; ctx.fillStyle = theme.text; ctx.globalAlpha = 0.7;
  ctx.font = `400 30px ${FONT}`;
  ctx.fillText(strings.footer, W / 2, H - 110);
  ctx.globalAlpha = 1;
}

export default function StoryExportModal({ open, onOpenChange, bio, name, lang = 'en' }) {
  const L = STORY_T[lang] || STORY_T.en;
  const ref = useRef(null);
  const [theme, setTheme] = useState(THEMES[0]);
  const [caption, setCaption] = useState(L.defaultCaption);
  const [stats, setStats] = useState(L.stats);
  const username = (name || 'yourname').toLowerCase().replace(/\s+/g, '.');
  const initial = (name || 'Y')[0].toUpperCase();

  const render = useCallback(() => {
    if (ref.current) drawStory(ref.current, { bio, username, initial, theme, caption, stats, strings: L });
  }, [bio, username, initial, theme, caption, stats, L]);

  const setStat = (i, pos) => (e) => {
    const v = e.target.value.slice(0, 12);
    setStats((s) => s.map((row, j) => (j === i ? (pos === 0 ? [v, row[1]] : [row[0], v]) : row)));
  };

  useEffect(() => {
    if (!open) return undefined;
    const id = setTimeout(render, 60);
    return () => clearTimeout(id);
  }, [open, render]);

  const getBlob = () => new Promise((res) => ref.current.toBlob(res, 'image/png'));

  const download = async () => {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bio-${username}-story.png`; a.click();
    URL.revokeObjectURL(url);
    toast.success(L.downloaded);
  };

  const share = async () => {
    const blob = await getBlob();
    const file = new File([blob], `bio-${username}-story.png`, { type: 'image/png' });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ files: [file], title: L.shareTitle }); } catch (e) { /* cancelled */ }
    } else {
      toast.info(L.shareFallback);
      download();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[760px] font-outfit max-h-[92vh] overflow-y-auto" data-testid="story-modal">
        <DialogHeader>
          <DialogTitle className="font-garamond text-2xl">{L.title}</DialogTitle>
          <DialogDescription>{L.desc}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-6 items-start">
          <canvas ref={ref} className="w-[240px] mx-auto rounded-xl border border-[var(--line)] shadow-lg" style={{ aspectRatio: '9 / 16' }} data-testid="story-canvas" />
          <div className="space-y-4">
            <div>
              <div className="upper-label mb-2">{L.bg}</div>
              <div className="flex flex-wrap gap-2">
                {THEMES.map((t) => (
                  <button key={t.id} onClick={() => setTheme(t)} title={L.themes[t.id]}
                    className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${theme.id === t.id ? 'border-[var(--gold)]' : 'border-white shadow'}`}
                    style={{ background: `linear-gradient(135deg, ${t.a}, ${t.b})` }} data-testid={`story-theme-${t.id}`} />
                ))}
              </div>
            </div>
            <label className="block">
              <span className="upper-label">{L.captionLabel}</span>
              <input value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 40))}
                className="mt-1 w-full rounded-md border border-[var(--line)] bg-white px-3 py-2 text-[14px] outline-none focus:border-[var(--gold2)]" data-testid="story-caption" />
            </label>
            <div>
              <span className="upper-label">{L.statsLabel}</span>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {stats.map(([v, l], i) => (
                  <div key={i} className="space-y-1">
                    <input value={v} onChange={setStat(i, 0)} aria-label={`${L.statsValue} ${i + 1}`}
                      className="w-full rounded-md border border-[var(--line)] bg-white px-2 py-1.5 text-[13px] font-semibold text-center outline-none focus:border-[var(--gold2)]" data-testid={`story-stat-value-${i}`} />
                    <input value={l} onChange={setStat(i, 1)} aria-label={`${L.statsName} ${i + 1}`}
                      className="w-full rounded-md border border-[var(--line)] bg-white px-2 py-1.5 text-[11.5px] text-center text-[var(--ink3)] outline-none focus:border-[var(--gold2)]" data-testid={`story-stat-label-${i}`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <button className="btn-gold inline-flex items-center justify-center gap-2 !py-2.5" onClick={download} data-testid="story-download"><Download className="w-4 h-4" />{L.download}</button>
              <button className="btn-outline inline-flex items-center justify-center gap-2 !py-2.5" onClick={share} data-testid="story-share"><Share2 className="w-4 h-4" />{L.share}</button>
            </div>
            <p className="text-[11.5px] text-[var(--ink4)]">{L.note}</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
